import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ChevronsUp, Search } from 'lucide-react';
import { useQueryClient } from 'react-query';
import { ItemWithRelations } from '../../../bf_api';
import { Category } from '../../../api/protosCompiled/buildFoundry/buildFoundry_pb';
import {
  useFetchBuildFoundryCategories,
  useFetchBuildFoundryItems,
} from '../../../hooks/useBuildFoundryHooks';
import { useToast } from '../../shared';
import {
  Box,
  Typography,
  Button,
  TextField,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import { ItemsList } from './ItemsList';
import { CategoryNode } from './CategoryNode';
import { instructionData } from '../../instructions/instructionData';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../../stores/store';
import { useFolderStore } from '../../../stores/folderStore';

export const communityItemsIndex = instructionData.findIndex(
  instruction => instruction.title === '🌍 Community Items'
);

interface FoldersViewProps {
  selectedItems: ItemWithRelations[];
  onItemSelection: (items: ItemWithRelations[]) => void;
  onCategorySelect?: (categoryId: string | null) => void;
  selectedCategoryId?: string | null;
}

export const FoldersView: React.FC<FoldersViewProps> = ({
  onCategorySelect,
  selectedCategoryId: externalSelectedCategoryId,
  selectedItems: externalSelectedItems,
  onItemSelection,
}) => {
  const theme = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Help system state
  const setIsHelpOpen = useStore(useUnityBuildStore, state => state.setIsHelpOpen);

  // Folder state management - using the same pattern as FolderNodes
  const openRows = useStore(useFolderStore, state => state.openRows);
  const toggleFolderExclusive = useStore(useFolderStore, state => state.toggleFolderExclusive);
  const closeAllFolders = useStore(useFolderStore, state => state.closeAllFolders);

  // Convert store's openRows format to Set format expected by CategoryNode
  const expandedNodes = useMemo(() => {
    return new Set(Object.keys(openRows).filter(key => openRows[key]));
  }, [openRows]);

  // Local state
  const [draggedNode, setDraggedNode] = useState<Category.AsObject | null>(null);
  const [dropTarget, setDropTarget] = useState<string | 'root' | null>(null);

  // Items state
  const [internalSelectedItems, setInternalSelectedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Use external selectedCategoryId if provided, otherwise use internal state
  const [internalSelectedCategoryId, setInternalSelectedCategoryId] = useState<string | null>(
    externalSelectedCategoryId ?? null
  );
  const selectedCategoryId =
    externalSelectedCategoryId !== undefined
      ? externalSelectedCategoryId
      : internalSelectedCategoryId;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // gRPC service calls for categories
  const { data: categoryTree, isLoading, error } = useFetchBuildFoundryCategories();

  // Auto-expand parent folders when a category is selected
  useEffect(() => {
    if (selectedCategoryId && categoryTree) {
      const expandParentFolders = (
        nodes: Category.AsObject[],
        targetId: string,
        currentPath: string[] = []
      ): boolean => {
        for (const node of nodes) {
          const newPath = [...currentPath, node.id];

          if (node.id === targetId) {
            // Found the target, expand all parent folders
            // Use the store's toggleFolderExclusive to maintain state consistency
            newPath.forEach(id => {
              if (!openRows[id]) {
                toggleFolderExclusive(id);
              }
            });
            return true;
          }

          if (node.childrenList && node.childrenList.length > 0) {
            const found = expandParentFolders(node.childrenList, targetId, newPath);
            if (found) return true;
          }
        }
        return false;
      };

      expandParentFolders(categoryTree.treeList, selectedCategoryId);
    }
  }, [selectedCategoryId, categoryTree, openRows, toggleFolderExclusive]);

  // Helper function to find category and check item count
  const findCategoryWithItemCount = useCallback(
    (categoryId: string) => {
      if (!categoryTree) return null;

      const findCategory = (nodes: Category.AsObject[]): Category.AsObject | null => {
        for (const node of nodes) {
          if (node.id === categoryId) {
            return node;
          }
          if (node.childrenList && node.childrenList.length > 0) {
            const found = findCategory(node.childrenList);
            if (found) return found;
          }
        }
        return null;
      };

      return findCategory(categoryTree.treeList);
    },
    [categoryTree]
  );

  // Get the selected category info
  const selectedCategory = findCategoryWithItemCount(selectedCategoryId || '');

  // gRPC service calls for items
  const {
    data: itemsData,
    isLoading: itemsLoading,
    error: itemsError,
  } = useFetchBuildFoundryItems(
    selectedCategoryId || undefined,
    currentPage,
    20, // perPage
    debouncedSearchTerm || undefined
  );

  // Handlers
  const handleToggleExpand = useCallback(
    (nodeId: string) => {
      // Use the store's toggleFolderExclusive to maintain state consistency
      toggleFolderExclusive(nodeId);
    },
    [toggleFolderExclusive]
  );

  const handleSelectCategory = useCallback(
    (categoryId: string) => {
      // Update internal state if no external handler
      if (onCategorySelect) {
        onCategorySelect(categoryId);
      } else {
        setInternalSelectedCategoryId(categoryId);
      }

      setSearchTerm(''); // Clear search when changing categories
      setCurrentPage(1); // Reset to first page
      setInternalSelectedItems(new Set()); // Clear item selection

      // Find category name for toast using the same approach
      const findCategoryName = (nodes: Category.AsObject[], targetId: string): string | null => {
        for (const node of nodes) {
          if (node.id === targetId) {
            return node.name;
          }
          if (node.childrenList && node.childrenList.length > 0) {
            const found = findCategoryName(node.childrenList, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      if (categoryTree) {
        const categoryName = findCategoryName(categoryTree.treeList, categoryId);
        if (categoryName) {
          toast.info(`Selected folder: ${categoryName}`);
        }
      }
    },
    [onCategorySelect, categoryTree, toast]
  );

  const handleCollapseAll = useCallback(() => {
    // Use the store's closeAllFolders to maintain state consistency
    closeAllFolders();
  }, [closeAllFolders]);

  // Item selection handlers
  const handleItemSelect = useCallback((itemId: string) => {
    setInternalSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAllItems = useCallback(() => {
    if (itemsData?.dataList) {
      const allItemIds = itemsData.dataList.map(item => item.id);
      setInternalSelectedItems(new Set(allItemIds));
    }
  }, [itemsData?.dataList]);

  const handleClearSelection = useCallback(() => {
    setInternalSelectedItems(new Set());
  }, []);

  // Pagination handler
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setInternalSelectedItems(new Set()); // Clear selection when changing pages
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Loading folders...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center', color: theme.palette.error.main }}>
          <Typography variant="body1">Failed to load folders</Typography>
          <Button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['categories'] })}
            variant="contained"
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden', // Prevents horizontal scroll unless truly needed
      }}
    >
      {/* Folders Panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${theme.palette.divider}`,
          flex: '0 0 33.333%',
          maxWidth: '33.333%',
          minWidth: 0, // Allow shrinking
        }}
      >
        {/* Header */}
        <Box
          sx={{
            flexShrink: 0,
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
              justifyContent: 'space-between',
            }}
          >
            {/* Collapse All */}
            <Button
              variant="outlined"
              onClick={handleCollapseAll}
              sx={{
                textTransform: 'none',
                minWidth: '32px',
                width: '32px',
                height: '40px',
                p: 0,
                '& .MuiButton-startIcon': {
                  margin: 0,
                },
              }}
            >
              <ChevronsUp size={12} />
            </Button>
            {/* Search Bar */}
            <Box sx={{ position: 'relative', flex: 1 }}>
              {itemsLoading ? (
                <CircularProgress
                  size={16}
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.palette.text.secondary,
                    zIndex: 1,
                  }}
                />
              ) : (
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.palette.text.secondary,
                    pointerEvents: 'none',
                  }}
                />
              )}
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={selectedCategoryId ? 'Search this folder...' : 'Select a folder...'}
                disabled={!selectedCategoryId}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    pl: 4,
                    pr: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                      bgcolor: alpha(theme.palette.action.disabled, 0.1),
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Tree View */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <Box sx={{ '&:hover .MuiIconButton-root': { opacity: 1 } }}>
            {categoryTree?.treeList
              .filter(node => node.id !== '32eed34b-c316-4d9d-8959-1e042fca0a71')
              .map(node => (
                <CategoryNode
                  key={node.id}
                  node={node}
                  level={0}
                  expandedNodes={expandedNodes}
                  onToggleExpand={handleToggleExpand}
                  onSelectCategory={handleSelectCategory}
                  selectedCategoryId={selectedCategoryId}
                  onContextMenu={() => {}}
                  dragState={{
                    draggedNode,
                    setDraggedNode,
                    dropTarget,
                    setDropTarget,
                  }}
                  isLoading={false}
                  operationsInProgress={new Set()}
                />
              ))}
          </Box>
        </Box>
      </Box>

      {/* Items Panel */}
      <Box
        sx={{
          flex: '1 1 0',
          minWidth: 0, // Allow shrinking
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selectedCategoryId ? (
          <>
            {itemsError ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Box sx={{ textAlign: 'center', color: theme.palette.error.main }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Failed to load items
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                    {itemsError instanceof Error ? itemsError.message : 'An error occurred'}
                  </Typography>
                  <Button
                    onClick={() =>
                      queryClient.invalidateQueries({
                        queryKey: ['items', selectedCategoryId, currentPage, debouncedSearchTerm],
                      })
                    }
                    variant="contained"
                    size="small"
                  >
                    Retry
                  </Button>
                </Box>
              </Box>
            ) : (
              <ItemsList
                items={itemsData?.dataList || []}
                selectedItems={internalSelectedItems}
                onItemSelect={handleItemSelect}
                onSelectAll={handleSelectAllItems}
                onClearSelection={handleClearSelection}
                onMoveItems={() => {}}
                categories={categoryTree?.treeList || []}
                isLoading={itemsLoading}
                currentPage={currentPage}
                totalPages={itemsData?.meta?.pages || 1}
                onPageChange={handlePageChange}
                searchTerm={debouncedSearchTerm}
                currentCategoryPath={selectedCategory?.path}
              />
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                color: theme.palette.text.secondary,
                px: 3,
                py: 4,
                maxWidth: '600px',
                margin: '0 auto',
              }}
            >
              {/* Main Heading */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: theme.palette.primary.main,
                  textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                Database in Development
              </Typography>

              {/* Info Box */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  mb: 3,
                }}
              >
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                  Items are available to add to assemblies, but may not have pricing or labor data
                  yet.
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  You can use override prices for these items in the closeout screen, though they
                  won't be persistent yet.
                </Typography>

                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  We're implementing persistent labor and pricing overrides that will contribute to
                  community values for these items.
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsHelpOpen(true);
                    localStorage.setItem('helpTargetSection', communityItemsIndex.toString());
                  }}
                  sx={{
                    mt: 2,
                    color: theme.palette.info.main,
                    borderColor: theme.palette.info.main,
                    '&:hover': {
                      borderColor: theme.palette.info.dark,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>

              {/* Folder Selection Context */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                  Select a folder to view items
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  Choose a category from the folder tree to load its items
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FoldersView;
